<template>
  <!--
    THE DEMO'S CAPTION BOARD (card D16, plan §4.11) — skin B, "Phosphor
    terminal" (the owner's pick): green phosphor on near-black, scanlines, a
    vignette, a `>` prompt, a block cursor, and a header that names the day and
    the simulated time (`DEMO // DAY 1 · 06:45`).

    Two hosts, one component (`variant`), the `DeviceWidget.mode` pattern:
    `scene` is the full board floating on the 3D house (bottom-left, above the
    scene and under the player's chrome), `card` is a one-line strip inside the
    demo FAB's mini player. The WORDS and the timing come from the hub (D14) and
    the pure rules (`utils/demoNarrationModel.ts`); this file only types them on.
  -->
  <div
    v-if="visible && caption"
    class="demo-narration"
    :class="[`demo-narration--${variant}`, { 'demo-narration--reduced': reducedMotion }]"
    :data-demo-narration-variant="variant"
    :data-demo-narration-cue="cue?.id ?? ''"
    :data-demo-narration-language="language"
    :data-demo-narration-seeking="cue?.seeking ? 'true' : 'false'"
    role="status"
    aria-live="polite">
    <div class="demo-narration__scanlines" aria-hidden="true"></div>
    <div class="demo-narration__vignette" aria-hidden="true"></div>

    <p class="demo-narration__head">{{ caption.header }}</p>

    <p class="demo-narration__line">
      <span class="demo-narration__prompt" aria-hidden="true">&gt;</span>
      <span class="demo-narration__text">{{ typed }}</span>
      <span class="demo-narration__cursor" aria-hidden="true"></span>
    </p>

    <p v-if="caption.data" class="demo-narration__data">{{ caption.data }}</p>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';

import { useDemoNarration } from '../../composables/useDemoNarration';
import { NARRATION_TYPE_MS } from '../../utils/demoNarrationModel';

withDefaults(
  defineProps<{
    /**
     * `scene` — the full board on the 3D house (header, scanlines, data line,
     * up to three lines of body). `card` — one line inside the mini player.
     */
    variant?: 'scene' | 'card';
  }>(),
  { variant: 'scene' },
);

/** The board itself: the cue, the three lines, and the language they are in. */
const { caption, visible, cue, language } = useDemoNarration();

/** `prefers-reduced-motion`: no typing, no blinking cursor — just the line. */
const reducedMotion = ref(false);

/** How much of the body has been typed on. */
const typed = ref('');

/** The typewriter's pending step. */
let stepTimer: ReturnType<typeof setTimeout> | null = null;

/** The media query we listen to (removed with the component). */
let motionQuery: MediaQueryList | null = null;

function motionQueryMatches(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function stopTyping(): void {
  if (stepTimer !== null) {
    clearTimeout(stepTimer);
    stepTimer = null;
  }
}

/**
 * Type the line on, one character every `NARRATION_TYPE_MS` — the skin's own
 * 26 ms. Reduced motion (or no text) puts the whole line up at once: the
 * caption is information first, and an animation nobody asked for is a bug.
 */
function typeOn(text: string): void {
  stopTyping();
  if (!text || reducedMotion.value) {
    typed.value = text;
    return;
  }

  const characters = Array.from(text);
  let index = 0;
  typed.value = '';
  const step = (): void => {
    index += 1;
    typed.value = characters.slice(0, index).join('');
    if (index < characters.length) stepTimer = setTimeout(step, NARRATION_TYPE_MS);
    else stepTimer = null;
  };
  stepTimer = setTimeout(step, NARRATION_TYPE_MS);
}

// A new cue (or a language switch, which is the same line in other words)
// retypes from the first character; the same line is never typed twice.
watch(
  () => caption.value?.body ?? '',
  (body) => typeOn(body),
  { immediate: true },
);

function onMotionPreferenceChange(): void {
  reducedMotion.value = motionQueryMatches();
  // Flipping to "reduce" mid-line finishes it rather than freezing a half line.
  if (reducedMotion.value) typeOn(caption.value?.body ?? '');
}

onMounted(() => {
  reducedMotion.value = motionQueryMatches();
  if (reducedMotion.value) typeOn(caption.value?.body ?? '');
  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (typeof motionQuery.addEventListener === 'function') {
      motionQuery.addEventListener('change', onMotionPreferenceChange);
    }
  }
});

onBeforeUnmount(() => {
  stopTyping();
  if (motionQuery && typeof motionQuery.removeEventListener === 'function') {
    motionQuery.removeEventListener('change', onMotionPreferenceChange);
  }
  motionQuery = null;
});
</script>

<style scoped>
/* ─── Phosphor CRT (skin B) ───────────────────────────────────────────────────
   Green on near-black, a faint scanline comb, a vignette that darkens the
   corners and a soft bloom on the glyphs. Values are the owner-approved
   reference (`demo-caption-styles.html`, case B) — keep them together so the
   skin can be recognised in one look. */
.demo-narration {
  --phosphor: #8dffa8;
  --phosphor-dim: rgba(125, 255, 168, 0.55);
  --phosphor-prompt: #4d9e6a;
  --phosphor-glow: rgba(90, 255, 150, 0.35);
  position: relative;
  isolation: isolate;
  overflow: hidden;
  border-radius: 6px;
  background: #030805;
  color: var(--phosphor);
  font-family: 'Cascadia Mono', 'Consolas', 'SFMono-Regular', ui-monospace, monospace;
  pointer-events: none;
}

.demo-narration__scanlines {
  position: absolute;
  inset: 0;
  z-index: -1;
  background:
    repeating-linear-gradient(0deg, rgba(140, 255, 180, 0.05) 0 1px, transparent 1px 4px),
    radial-gradient(120% 130% at 50% 40%, #0a1a10 0%, #050c08 60%, #020604 100%);
}

/* The vignette: the CRT's own fall-off, so the board never looks like a flat
   panel pasted on the house. */
.demo-narration__vignette {
  position: absolute;
  inset: 0;
  z-index: -1;
  background: radial-gradient(115% 120% at 50% 45%, transparent 45%, rgba(0, 0, 0, 0.55) 100%);
}

.demo-narration__head {
  margin: 0;
  font-size: 9.5px;
  letter-spacing: 0.2em;
  line-height: 1.4;
  color: var(--phosphor-dim);
  text-transform: uppercase;
}

.demo-narration__line {
  margin: 0;
  font-size: 15px;
  line-height: 1.5;
  color: var(--phosphor);
  text-shadow: 0 0 8px var(--phosphor-glow);
  /* Max three lines, the skin's own rule: a longer caption clamps rather than
     growing a wall of text over the house. */
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.demo-narration__prompt {
  margin-right: 7px;
  color: var(--phosphor-prompt);
}

/* The block cursor: the board is "waiting for the next line" between words. */
.demo-narration__cursor {
  display: inline-block;
  width: 8px;
  height: 15px;
  margin-left: 3px;
  vertical-align: -2px;
  background: var(--phosphor);
  animation: demo-narration-blink 1.05s steps(1) infinite;
}

.demo-narration__data {
  margin: 6px 0 0;
  font-size: 10px;
  letter-spacing: 0.12em;
  line-height: 1.4;
  color: var(--phosphor-dim);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@keyframes demo-narration-blink {
  50% {
    opacity: 0;
  }
}

/* ─── scene: the board on the 3D house ───────────────────────────────────────
   Bottom-left, above the canvas (z 30 — under the toolbar's 200 and under the
   positioned bars), clear of the viewpoint strip on a phone (which owns the
   bottom edge below 640 px). It never eats a pointer event: the model stays
   draggable through the caption. */
.demo-narration--scene {
  position: absolute;
  left: 16px;
  bottom: 18px;
  z-index: 30;
  max-width: min(560px, calc(100% - 32px));
  padding: 10px 14px 12px;
  border: 1px solid rgba(125, 255, 168, 0.16);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.45);
}

@media (max-width: 640px) {
  .demo-narration--scene {
    left: 12px;
    right: 12px;
    bottom: 64px;
    max-width: none;
  }
}

/* ─── card: one line inside the mini player ──────────────────────────────────
   The same phosphor, one row: no header, no data line, ellipsis instead of a
   second line, and no cursor flicker to pull the eye off the clock beside it. */
.demo-narration--card {
  padding: 5px 10px 6px;
  border-radius: 8px;
}

.demo-narration--card .demo-narration__head,
.demo-narration--card .demo-narration__data,
.demo-narration--card .demo-narration__cursor {
  display: none;
}

.demo-narration--card .demo-narration__line {
  display: block;
  font-size: 12px;
  line-height: 1.4;
  white-space: nowrap;
  text-overflow: ellipsis;
  -webkit-line-clamp: 1;
}

/* ─── reduced motion ─────────────────────────────────────────────────────────
   No flicker, no typing: the line is simply there (the typewriter is skipped in
   the script as well, so a reduced-motion reader never waits for it). */
.demo-narration--reduced .demo-narration__cursor {
  animation: none;
  opacity: 1;
}
</style>
